import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

class LocalPickup extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @type {(event: VariantUpdateEvent | CustomEvent) => void} */
    const variantUpdated = (event) => {
      if (event.detail?.data?.newProduct) {
        this.dataset.productUrl = event.detail.data.newProduct.url;
      }

      const resource = event.detail?.resource || event.detail?.variant;
      const variantId = resource ? String(resource.id) : null;
      const variantAvailable = resource ? Boolean(resource.available) : null;
      if (variantId && variantId !== this.dataset.variantId) {
        this.dataset.variantId = variantId;
        if (variantAvailable) {
          this.#fetchAvailability(variantId);
        } else {
          this.setAttribute('hidden', '');
        }
      }
    };

    const targetElement = closestSection || document;
    targetElement.addEventListener(ThemeEvents.variantUpdate, variantUpdated);
    document.addEventListener('variant:change', variantUpdated);

    this.disconnectedCallback = () => {
      super.disconnectedCallback();
      targetElement.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
      document.removeEventListener('variant:change', variantUpdated);
    };
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Fetches the availability of a variant.
   * @param {string} variantId - The ID of the variant to fetch availability for.
   */
  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();

    const url = this.dataset.productUrl;
    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const wrapper =
          html.querySelector(`local-pickup[data-variant-id="${variantId}"]`) ||
          html.querySelector('local-pickup');
        if (wrapper) {
          if (wrapper.hasAttribute('hidden') || !wrapper.innerHTML.trim()) {
            this.setAttribute('hidden', '');
          } else {
            this.removeAttribute('hidden');
          }
          morph(this, wrapper);

          // Synchronize external drawer if present
          const drawerBtn = this.querySelector('[on\\:click*="#pickup-drawer-"]');
          const drawerMatch = drawerBtn?.getAttribute('on:click')?.match(/#(pickup-drawer-[^/]+)/);
          if (drawerMatch && drawerMatch[1]) {
            const drawerId = drawerMatch[1];
            const currentDrawer = document.getElementById(drawerId);
            const newDrawer = html.getElementById(drawerId);
            if (currentDrawer && newDrawer) {
              morph(currentDrawer, newDrawer);
            } else if (!currentDrawer && newDrawer) {
              this.insertAdjacentElement('afterend', newDrawer);
            } else if (currentDrawer && !newDrawer) {
              currentDrawer.remove();
            }
          }
        } else {
          this.setAttribute('hidden', '');
        }
      })
      .catch((_e) => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  };
}

if (!customElements.get('local-pickup')) {
  customElements.define('local-pickup', LocalPickup);
}

